import { Link } from 'react-router-dom';
import { Bell, Shield, Key, FileWarning, Settings, Clock, Globe, Server, Check, HashIcon } from 'lucide-react';
import { BaseNotification, FindingNotification, AssetNotification } from '../types/notifications';
import { useTeams } from '@/contexts/TeamsContext';
import { AuthContext } from '@/contexts/AuthContext';
import { Notification, NotificationsApi } from 'api-client';
import { getApiConfiguration } from '@/api/utils';
import React, { useCallback, useContext, useEffect, useState } from 'react';

// Define extended notification type with summary field
interface ExtendedNotification extends Notification {
  summary?: {
    total: number;
    bySeverity?: {
      critical: number;
      high: number;
      medium: number;
      low: number;
    };
    byType?: {
      new: number;
      modified: number;
      deleted: number;
      exposed: number;
    };
  };
}

// Helper function to format timestamp for display
const formatTimestamp = (timestamp: Date | string | undefined): string => {
  if (!timestamp) return '';
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

// Helper function to format timestamp for API
const formatNotificationForUpdate = (notification: ExtendedNotification): any => {
  return {
    ...notification,
    // Convert Date to ISO string for API
    timestamp: notification.timestamp instanceof Date ? notification.timestamp.toISOString() : notification.timestamp,
  };
};

const AllNotifications: React.FC = () => {
  const { currentTeam } = useTeams();
  const { token } = useContext(AuthContext);
  const client = new NotificationsApi(getApiConfiguration(token));
  const [notifications, setNotifications] = useState<ExtendedNotification[]>([]);
  
  const fetchNotifications = useCallback(async () => {
    try {
      if (!currentTeam?.slug) return;
      const response = await client.notificationsNotificationsList({ teamSlug: currentTeam?.slug });
      
      // Process notifications to add summary data
      const processedNotifications = response.results.map(notification => {
        const extendedNotification: ExtendedNotification = {
          ...notification,
          summary: undefined
        };
        
        // Extract count from message if available
        let count = 1; // Default to 1 if we can't extract a count
        const messageMatch = notification.message?.match(/(\d+)/);
        if (messageMatch && messageMatch[1]) {
          count = parseInt(messageMatch[1], 10);
        }
        
        // Add summary based on notification type
        if (notification.objectType === 'Finding') {
          // Calculate summary for findings based on severity
          const severityCounts = {
            critical: 0,
            high: 0,
            medium: 0,
            low: 0
          };
          
          // Set count based on severity
          const severity = notification.severity?.toLowerCase() || '';
          if (severity === 'critical') severityCounts.critical = count;
          else if (severity === 'high') severityCounts.high = count;
          else if (severity === 'medium') severityCounts.medium = count;
          else if (severity === 'low') severityCounts.low = count;
          
          // Set the summary
          extendedNotification.summary = {
            total: count,
            bySeverity: severityCounts
          };
        } else if (['Domain', 'Subdomain', 'Ip_Address', 'Ports'].includes(notification.objectType)) {
          // Calculate summary for assets based on event type
          const typeCounts = {
            new: 0,
            modified: 0,
            deleted: 0,
            exposed: 0
          };
          
          // Map event types to summary types
          const eventType = notification.eventType?.toLowerCase() || '';
          if (eventType === 'discovered') typeCounts.new = count;
          else if (eventType === 'updated') typeCounts.modified = count;
          else if (eventType === 'deleted') typeCounts.deleted = count;
          else if (eventType === 'exposure') typeCounts.exposed = count;
          
          // Set the summary
          extendedNotification.summary = {
            total: count,
            byType: typeCounts
          };
        } else if (['Publicleak', 'Stealerleak', 'Dataleak'].includes(notification.objectType)) {
          // Calculate summary for leaks
          const typeCounts = {
            new: 0,
            modified: 0,
            deleted: 0,
            exposed: 0
          };
          
          // Map event types to summary types
          const eventType = notification.eventType?.toLowerCase() || '';
          if (eventType === 'discovered') typeCounts.new = count;
          else if (eventType === 'updated') typeCounts.modified = count;
          else if (eventType === 'deleted') typeCounts.deleted = count;
          else if (eventType === 'exposure') typeCounts.exposed = count;
          
          // Set the summary
          extendedNotification.summary = {
            total: count,
            byType: typeCounts
          };
        }
        
        return extendedNotification;
      });
      
      setNotifications(processedNotifications);
    } catch (error) {
      console.log('Error fetching notifications:', error);
    }
  }, [currentTeam?.slug, token]);

  const handleReadNotification = (notificationId: number, notification: ExtendedNotification) => {
    if (!token || !currentTeam?.slug || !notificationId) return;

    // Format notification before sending to API
    const formattedNotification = formatNotificationForUpdate(notification);
    client.notificationsNotificationsUpdate({ 
      teamSlug: currentTeam?.slug, 
      id: notificationId.toString(), 
      notification: { ...formattedNotification, read: true } 
    });
    setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n));
  };

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);
  
  const getNotificationIcon = (notification: ExtendedNotification) => {
    switch (notification.objectType) {
      case 'Domain': return Globe;
      case 'Subdomain': return Globe;
      case 'Ip_Address': return Globe;
      case 'Ports': return Globe;
      case "Finding": return Shield;
      case "Publicleak": return Key;
      case "Stealerleak": return Key;
      case "Dataleak": return HashIcon;
      default: return Bell;
    }

    // // Finding category
    // switch (notification.type) {
    //   case 'security': return Shield;
    //   case 'credential': return Key;
    //   case 'data': return FileWarning;
    //   case 'system': return Settings;
    //   default: return Bell;
    // }
  };

  const getNotificationColor = (notification: ExtendedNotification) => {
    switch (notification.severity) {
      case 'critical': return 'text-red-500 bg-red-500/10';
      case 'high': return 'text-orange-500 bg-orange-500/10';
      case 'medium': return 'text-yellow-500 bg-yellow-500/10';
      case 'low': return 'text-emerald-500 bg-emerald-500/10';
      case "Finding": return 'text-red-500 bg-red-500/10';
      case "Publicleak": return 'text-blue-500 bg-blue-500/10';
      case "Stealerleak": return 'text-blue-500 bg-blue-500/10';
      case "Dataleak": return 'text-blue-500 bg-blue-500/10';
      // case 'high': return 'text-red-500 bg-red-500/10';
      // case 'info': return 'text-blue-500 bg-blue-500/10';
      // case 'medium': return 'text-purple-500 bg-purple-500/10';
      // case 'low': return 'text-emerald-500 bg-emerald-500/10';
      // default: return 'text-gray-500 bg-gray-500/10';
      default: return 'text-blue-500 bg-blue-500/10';
    }
    
    // Asset category
    // switch (notification.eventType?.toLowerCase()) {
    //   case 'updated': return 'text-blue-500 bg-blue-500/10';
    //   case 'discovered': return 'text-yellow-500 bg-yellow-500/10';
    //   case 'exposure': return 'text-red-500 bg-red-500/10';
    //   default: return 'text-gray-500 bg-gray-500/10';
    // }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">All Notifications</h1>
        <div className="text-sm text-gray-400">
          {notifications.filter(n => !n.read).length} unread
        </div>
      </div>

      <div className="bg-[#1A1F2E] rounded-lg border border-gray-800">
        {notifications.map((notification, index) => {
          const Icon = getNotificationIcon(notification);
          const colorClass = getNotificationColor(notification);
          const isLast = index === notifications.length - 1;

          return (
            <Link
              key={notification.id}
              to={`/notifications/${notification.objectType}/${notification.id}`}
              onClick={() => handleReadNotification(notification.id, notification)}
              className={`block ${!isLast ? 'border-b border-gray-800' : ''}`}
            >
              <div className="p-4 hover:bg-gray-800/50 transition-colors">
                <div className="flex items-start space-x-4">
                  <div className={`p-2 rounded-lg ${colorClass}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="text-md font-medium text-white">
                        {notification.message}
                      </h3>
                      <div className="flex items-center space-x-3">
                        {notification.read && (
                          <div className="flex items-center text-emerald-500 text-sm">
                            <Check className="h-4 w-4 mr-1" />
                            Read
                          </div>
                        )}
                        <div className="flex items-center text-gray-500 text-sm">
                          <Clock className="h-4 w-4 mr-1" />
                          {formatTimestamp(notification.timestamp)}
                        </div>
                      </div>
                    </div>
                    
                    {/* <p className="text-gray-400 text-sm mt-1">
                      {notification.description}
                    </p> */}

                    <div className="mt-2 flex flex-wrap gap-2">
                      {notification.objectType === 'Finding' && notification.summary && (
                        <>
                          <span className="text-xs px-2 py-1 rounded-full bg-gray-800 text-gray-400">
                            {notification.summary.total} findings
                          </span>
                          {Object.entries(notification?.summary?.bySeverity || {}).map(([severity, count]) => {
                            if (count === 0) return null;
                            const color = severity === 'critical' ? 'red' : severity === 'high' ? 'orange' : severity === 'medium' ? 'yellow' : 'green';
                            return (
                              <span key={severity} className={`text-xs px-2 py-1 rounded-full bg-${color}-500/10 text-${color}-500`}>
                                {count} {severity}
                              </span>
                            );
                          })}
                        </>
                      )}
                      {['Domain', "Subdomain", "IP_Address", "Ports"].includes(notification.objectType) && notification.summary && (
                        <>
                          <span className="text-xs px-2 py-1 rounded-full bg-gray-800 text-gray-400">
                            {notification.summary.total} changes
                          </span>
                          {Object.entries(notification?.summary?.byType || {}).map(([type, count]) => {
                            if (count === 0) return null;
                            const color = type === 'new' ? 'blue' : type === 'modified' ? 'yellow' : type === 'deleted' ? 'red' : 'green';
                            return (
                              <span key={type} className={`text-xs px-2 py-1 rounded-full bg-${color}-500/10 text-${color}-500`}>
                                {count} {type}
                              </span>
                            );
                          })}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default AllNotifications;
